import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { useCallback } from 'react';

import { NewItemCardRegisterLink } from '@/components/GridItemCard/Card/components/NewItemCardRegisterLink/NewItemCardRegisterLink';
import PlaceBidButton from '@/components/GridItemCard/Card/components/PlaceBidButton/PlaceBidButton';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getIsRegistered,
    getRegistrationApproved,
    getRegistrationDeclined,
    getRegistrationPending,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getItemSummaryBidderHasBid,
    getItemSummaryCatalogId,
    getItemSummaryCatalogOnly,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { openRetractBidModal } from '@/redux/modules/modal';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

export type CardRegistrationProps = {
    isMobile: boolean;
    itemId: number;
};

export const CardRegistration = ({ isMobile, itemId }: CardRegistrationProps) => {
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();

    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryCatalogOnly(state, itemId));
    const bidderHasBid = useAppSelector((state) => getItemSummaryBidderHasBid(state, itemId));

    const approved = useAppSelector((state) => getRegistrationApproved(state, catalogId));
    const registered = useAppSelector((state) => getIsRegistered(state, catalogId));
    const pending = useAppSelector((state) => getRegistrationPending(state, catalogId));
    const declined = useAppSelector((state) => getRegistrationDeclined(state, catalogId));

    const openRetractBidModalCallback = useCallback(() => {
        dispatch(openRetractBidModal(itemId));
    }, [dispatch, itemId]);

    return (
        <section
            className="flex max-h-[34px] [grid-area:registerForAuction]"
            data-testid="myItemsCardRegistration"
        >
            {!declined && !pending && !isCatalogOnly && <PlaceBidButton itemId={itemId} />}
            {(!approved || isCatalogOnly) && (
                <NewItemCardRegisterLink
                    catalogId={catalogId}
                    itemId={itemId}
                    registerToBidMessageId="registration.register_for_auction_my_items"
                />
            )}
            {(approved || registered) && bidderHasBid && isMobile && !pending && (
                <Button
                    aria-label="retract bid button"
                    className="max-[560px]:px-xs xsMax:px-sm"
                    onClick={openRetractBidModalCallback}
                    size="sm"
                >
                    {formatMessage(['retract'])}
                </Button>
            )}
        </section>
    );
};
